import { GRID_SIZE } from '../game/constants';
import { key } from '../game/geometry';
import { mulberry32 } from '../game/rng';
import type { ObstacleMap } from '../game/types';

/*
 * The water contour (STYLEGUIDE §4): one closed path per water body, rounded
 * at the coves and bays, with a quiet seeded wobble along the straight runs —
 * a river has drift, a rounded-rectangle corridor is a UI element. The same
 * geometry is drawn twice by the board: filled as the water itself and
 * stroked as the shoreline, so the line and the fill can never disagree.
 * Pure geometry over the day's obstacle map — deterministic for everyone,
 * because the wobble is seeded from the map, never from the clock.
 */

/** Corner radius, in board units — a real cove, not a chamfer. */
export const COVE_R = 24;

/**
 * How far the drawn world extends past the surveyed plot, in board units.
 * Water that reaches the plot edge continues into this margin, so the frame
 * crops a world instead of ending a corridor.
 */
export interface ShoreMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const NO_MARGINS: ShoreMargins = { top: 0, right: 0, bottom: 0, left: 0 };

/** Peak wobble off the straight line, in board units. Most points land far
    inside this: the square-biased draw below keeps long stretches calm and
    spends the full amplitude only on the occasional bulge — uniform wobble
    reads as a noise filter, not a hand. Small even at peak: which cell is
    water must never blur — water is blocked terrain, and legibility is
    gameplay. */
const AMP = 8;

/** One wobble control point per this many board units of straight run.
    Unhurried on purpose — a busy shoreline is the same noise-filter tell
    that uniform amplitude is. */
const WOBBLE_STEP = 85;

/** Runs shorter than this stay straight — nothing to say at that length. */
const MIN_WOBBLE = 34;

interface Vec {
  x: number;
  y: number;
}

const r1 = (v: number): number => Math.round(v * 10) / 10;

/**
 * The water contour path for the whole board, as SVG path data in board
 * units (`size` = GRID_SIZE * CELL). Empty string on a board with no water.
 *
 * The walk traces each body clockwise (water on the right of travel), so an
 * island ringed by water winds the opposite way and the default nonzero fill
 * rule leaves it dry. Each 90° corner is classified by the count of water
 * cells among the four around its vertex:
 *
 * - 1 water  → a convex cove → rounded.
 * - 3 water  → a concave bay → rounded, unless the lone land cell is a
 *   mountain — the ridge silhouette claims that cell's corners, and a bay
 *   arc under a peak confuses the read.
 * - 2 water on a diagonal → a pinch: square, and each body keeps its own
 *   contour.
 *
 * Out of board counts as land, so contours close along the map edge; with
 * `margins`, the runs that would hug the plot edge are pushed out to the
 * margin's crop line instead, and stay straight — a run on the frame is the
 * world ending, not a shoreline.
 */
export function shorelinePath(
  obstacles: ObstacleMap,
  size: number,
  margins: ShoreMargins = NO_MARGINS,
): string {
  const cell = size / GRID_SIZE;
  const mg = margins;
  const isWater = (r: number, c: number): boolean =>
    r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE
      ? obstacles[key({ r, c })] === 'water'
      : false;
  const isMtn = (r: number, c: number): boolean =>
    r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE && obstacles[key({ r, c })] === 'mtn';

  // Directed unit edges, clockwise around each water cell in screen
  // coordinates (water on the right of travel), keyed by start vertex.
  // Vertices are lattice points in cell units: (x, y) = (column, row).
  interface Edge {
    from: Vec;
    to: Vec;
    /** The water cell this edge bounds — the saddle rule reads it. */
    cell: string;
  }
  const outgoing = new Map<string, Edge[]>();
  const vk = (v: Vec): string => `${v.x},${v.y}`;
  const add = (from: Vec, to: Vec, cellKey: string): void => {
    const e = { from, to, cell: cellKey };
    const list = outgoing.get(vk(from));
    if (list) list.push(e);
    else outgoing.set(vk(from), [e]);
  };
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (!isWater(r, c)) continue;
      const cellKey = key({ r, c });
      if (!isWater(r - 1, c)) add({ x: c, y: r }, { x: c + 1, y: r }, cellKey);
      if (!isWater(r, c + 1)) add({ x: c + 1, y: r }, { x: c + 1, y: r + 1 }, cellKey);
      if (!isWater(r + 1, c)) add({ x: c + 1, y: r + 1 }, { x: c, y: r + 1 }, cellKey);
      if (!isWater(r, c - 1)) add({ x: c, y: r + 1 }, { x: c, y: r }, cellKey);
    }
  }

  // Stitch closed contours. At a saddle vertex (two bodies touching
  // diagonally — four edges meet) the successor bounding the same water cell
  // keeps the walk hugging the body it is skirting, so each body keeps its
  // own square corner and two touching bodies stay two contours.
  const used = new Set<Edge>();
  const contours: Vec[][] = [];
  for (const list of outgoing.values()) {
    for (const first of list) {
      if (used.has(first)) continue;
      const loop: Vec[] = [];
      let e = first;
      for (;;) {
        used.add(e);
        loop.push(e.from);
        if (e.to.x === first.from.x && e.to.y === first.from.y) break;
        const nexts = (outgoing.get(vk(e.to)) ?? []).filter((n) => !used.has(n));
        const next = nexts.find((n) => n.cell === e.cell) ?? nexts[0];
        /* v8 ignore next -- unreachable on a well-formed map; a guard, not a path */
        if (!next) break;
        e = next;
      }
      contours.push(loop);
    }
  }

  const waterCount = (v: Vec): number =>
    [
      isWater(v.y - 1, v.x - 1),
      isWater(v.y - 1, v.x),
      isWater(v.y, v.x - 1),
      isWater(v.y, v.x),
    ].filter(Boolean).length;

  const rounded = (v: Vec): boolean => {
    const count = waterCount(v);
    if (count === 1) return true; // cove
    if (count === 3) {
      // The lone land cell is always in-board: a board-edge vertex has two
      // out-of-board (land) cells around it, so it can never count 3.
      const cells: [number, number][] = [
        [v.y - 1, v.x - 1],
        [v.y - 1, v.x],
        [v.y, v.x - 1],
        [v.y, v.x],
      ];
      const land = cells.find(([r, c]) => !isWater(r, c))!;
      return !isMtn(land[0], land[1]); // bay, unless a peak owns the corner
    }
    return false; // the diagonal saddle stays square
  };

  // A lattice vertex's board position, with plot-edge vertices pushed out to
  // the margin's crop line — the world continues where the survey stops.
  const bx = (v: Vec): number =>
    v.x === 0 ? -mg.left : v.x === GRID_SIZE ? size + mg.right : v.x * cell;
  const by = (v: Vec): number =>
    v.y === 0 ? -mg.top : v.y === GRID_SIZE ? size + mg.bottom : v.y * cell;

  // The organic run: a straight segment redrawn as a quiet meander. Control
  // points jitter off the line, smoothed through their midpoints so the
  // curve stays tangent-continuous; the seed comes from the endpoints, so
  // the same map draws the same shore on every device. Runs lying on the
  // world's crop line stay straight — the frame ends the world, the shore
  // doesn't wobble along it.
  const wobble = (S: Vec, E: Vec): string => {
    const dx = E.x - S.x;
    const dy = E.y - S.y;
    const horiz = dy === 0;
    const len = Math.abs(dx + dy); // axis-aligned: the other term is 0
    const line = `L${r1(E.x)} ${r1(E.y)}`;
    const onFrame = horiz
      ? S.y === -mg.top || S.y === size + mg.bottom
      : S.x === -mg.left || S.x === size + mg.right;
    if (onFrame || len < MIN_WOBBLE) return line;
    const n = Math.max(1, Math.round(len / WOBBLE_STEP));
    const rnd = mulberry32(
      (Math.round(S.x) * 73856093) ^
        (Math.round(S.y) * 19349663) ^
        (Math.round(E.x) * 83492791) ^
        (Math.round(E.y) * 15485863),
    );
    const pts: Vec[] = [];
    for (let i = 1; i <= n; i++) {
      const t = (i + (rnd() - 0.5) * 0.6) / (n + 1);
      // Two uniform draws multiplied bias the magnitude hard toward zero,
      // so most of the run hugs the line and a rare point swings wide.
      const sign = rnd() < 0.5 ? -1 : 1;
      const off = sign * AMP * rnd() * rnd();
      pts.push(horiz ? { x: S.x + dx * t, y: S.y + off } : { x: S.x + off, y: S.y + dy * t });
    }
    let out = '';
    for (let i = 0; i < pts.length; i++) {
      const c = pts[i];
      const end =
        i === pts.length - 1 ? E : { x: (c.x + pts[i + 1].x) / 2, y: (c.y + pts[i + 1].y) / 2 };
      out += `Q${r1(c.x)} ${r1(c.y)} ${r1(end.x)} ${r1(end.y)}`;
    }
    return out;
  };

  const subpaths: string[] = [];
  for (const loop of contours) {
    // Merge collinear runs; what remains are the genuine 90° corners.
    const n = loop.length;
    const corners: Vec[] = [];
    for (let i = 0; i < n; i++) {
      const prev = loop[(i + n - 1) % n];
      const cur = loop[i];
      const nxt = loop[(i + 1) % n];
      const turn =
        Math.sign(cur.x - prev.x) !== Math.sign(nxt.x - cur.x) ||
        Math.sign(cur.y - prev.y) !== Math.sign(nxt.y - cur.y);
      if (turn) corners.push(cur);
    }

    const m = corners.length;
    // The corner's fillet: trim COVE_R off both runs, join with a quarter
    // arc whose sweep follows the turn's orientation. Runs between corners
    // are at least one cell (> 2R), so the trims never collide. A square
    // corner keeps its point: entry and exit collapse onto it.
    const cut = (i: number): { a: Vec; b: Vec; arc: string } => {
      const cur = corners[i];
      const P = { x: bx(cur), y: by(cur) };
      const Pp = { x: bx(corners[(i + m - 1) % m]), y: by(corners[(i + m - 1) % m]) };
      const Pn = { x: bx(corners[(i + 1) % m]), y: by(corners[(i + 1) % m]) };
      const inD = { x: Math.sign(P.x - Pp.x), y: Math.sign(P.y - Pp.y) };
      const outD = { x: Math.sign(Pn.x - P.x), y: Math.sign(Pn.y - P.y) };
      if (!rounded(cur)) return { a: P, b: P, arc: '' };
      const a = { x: P.x - inD.x * COVE_R, y: P.y - inD.y * COVE_R };
      const b = { x: P.x + outD.x * COVE_R, y: P.y + outD.y * COVE_R };
      const sweep = inD.x * outD.y - inD.y * outD.x > 0 ? 1 : 0;
      return { a, b, arc: `A${COVE_R} ${COVE_R} 0 0 ${sweep} ${r1(b.x)} ${r1(b.y)}` };
    };

    // Open at corner 0's exit and walk every run and fillet after it; the
    // final run comes back onto the start point (via corner 0's own fillet
    // where it has one), and Z closes a loop that is already home.
    const cuts = corners.map((_, i) => cut(i));
    let d = `M${r1(cuts[0].b.x)} ${r1(cuts[0].b.y)}`;
    for (let i = 1; i <= m; i++) {
      const c = cuts[i % m];
      d += wobble(cuts[i - 1].b, c.a) + c.arc;
    }
    subpaths.push(d + 'Z');
  }
  return subpaths.join('');
}
